<template>
  <div class="authorize-page container">
    <h1>Authorize app</h1>
    <Spinner v-if="busyRef"/>
    <div v-else-if="errorRef" class="error">
      {{ errorRef }}
    </div>
    <template v-else>
      <img v-if="clientIcon" :src="clientIcon" class="client-icon">
      <div class="consent-message">
        <strong>{{ clientNameRef }}</strong>
        <p>wants to access your Tree Crossing account.</p>
      </div>
      <div class="actions">
        <button class="btn btn-primary" @click="allow">Allow</button>
        <button class="btn btn-outline-secondary" @click="deny">Deny</button>
      </div>
    </template>
  </div>
</template>

<style lang="scss" scoped>
.authorize-page {
  margin-bottom: 2rem;
  padding-top: 1rem;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1rem;

  @media (min-width: 768px) {
    max-width: 600px;
    min-height: 500px;

    &:not(.embedded) {
      margin-top: 4rem;
      border: 1px solid #ddd;
      border-radius: 1rem;
      padding: 1rem 2rem 2rem 2rem;
    }
  }
}

.client-icon {
  width: 100px;  
}

.consent-message {
  margin-bottom: 2rem;
  text-align: center;
}

.actions {
  display: flex;
  justify-content: center;
  gap: 1rem;

  .btn {
    min-width: 80px;
  }
}

.error {
  color: var(--bs-danger);
}
</style>

<script lang="ts" setup>
import { computed, onMounted, ref } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useTokenManager } from './TokenManager'
import { useTelemetry } from '@/util/Telemetry'
import { LocalTokenApi, tokenClientIcons } from './LocalTokenApi'
import { usePageTitle } from '@/util/AppUtil'
import Spinner from '@/util/Spinner.vue'

const busyRef = ref(true)
const errorRef = ref<string>()
const clientNameRef = ref<string>()

const route = useRoute()
const router = useRouter()
const tokenManager = useTokenManager()
const { trackException } = useTelemetry()

usePageTitle('Authorize app')

const clientId = computed(() => route.query.client_id as string)
const redirectUri = computed(() => route.query.redirect_uri as string)
const codeChallenge = computed(() => route.query.code_challenge as string)
const codeChallengeMethod = computed(() => route.query.code_challenge_method as string)
const state = computed(() => route.query.state as string)
const clientIcon = computed(() => tokenClientIcons.get(clientId.value))

function redirectWithParams(params: Record<string, string | undefined>) {
  const url = new URL(redirectUri.value)
  for (const [key, value] of Object.entries(params)) {
    if (value) url.searchParams.set(key, value)
  }
  window.location.href = url.toString()
}

onMounted(async () => {
  if (!tokenManager.current.isValid) {
    // Try refreshing an expired token before redirecting to sign in
    if (!await tokenManager.ensureRefreshedAsync()) {
      router.push({ path: '/signin', query: { redirect_uri: route.fullPath } })
      return
    }
  }

  if (!redirectUri.value || !codeChallenge.value || !codeChallengeMethod.value) {
    errorRef.value = 'Invalid authorization request.'
    busyRef.value = false
    return
  }

  try {
    const clientInfo = await LocalTokenApi.getTokenClientAsync(tokenManager.current.token, clientId.value)
    clientNameRef.value = clientInfo.name
  }
  catch {
    errorRef.value = 'Unknown application'
  }

  busyRef.value = false
})

async function allow() {
  try {
    busyRef.value = true
    const result = await LocalTokenApi.getLocalAuthCodeAsync(
      tokenManager.current.token, clientId.value, {
        redirectUri: redirectUri.value,
        codeChallenge: codeChallenge.value,
        codeChallengeMethod: codeChallengeMethod.value,
      })

    redirectWithParams({ code: result.code, state: state.value })
  }
  catch (error) {
    trackException(error)
    errorRef.value = 'Something went wrong. Please try again.'
    busyRef.value = false
  }
}

function deny() {
  redirectWithParams({ error: 'access_denied', state: state.value })
}
</script>
